/**
 * IFC Export for Interoperability
 *
 * Writes a schema-valid IFC4 STEP document that third-party readers (the
 * CoordinationView workflow) can open: full owner-history boilerplate,
 * geometric representation context with SI units, the spatial hierarchy
 * (Project → Site → Building → Storeys via IfcRelAggregates) and swept-solid
 * bodies for the supported categories (IfcRelContainedInSpatialStructure).
 * Elements whose geometry this writer cannot express come through as
 * placement-only products — structurally valid, never schema-broken.
 */

import { Project, BIMElement } from '../core/types';

/** Export mapping configuration */
export const IFC_EXPORT_SETTINGS = {
    exportBaseQuantities: true,
    exportBoundingBox: true,
    use2DForPlanViews: false,
    exportLinkedFiles: true,
    splitWallsAndColumns: true,
    exportRooms: true,
    exportAreas: true,
    exportSchedules: false,
    ifcVersion: 'IFC4' as const
};

// ==================== STEP primitives ====================

const PRECISION = 1e-5;

/** STEP real: always decimal point, trimmed to 6 decimals (metres). */
function num(v: number): string {
    if (!isFinite(v)) v = 0;
    const s = Math.abs(v) < PRECISION ? '0.' : v.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.');
    return s.includes('.') ? s : `${s}.`;
}

/** STEP string literal with quote escaping. */
function str(s: string): string {
    return `'${String(s ?? '').replace(/'/g, "''")}'`;
}

/** IFC GlobalId: 22 chars of the IFC base64 alphabet. */
function generateGlobalId(): string {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$';
    let id = '';
    for (let i = 0; i < 22; i++) {
        id += chars[Math.floor(Math.random() * 64)];
    }
    return id;
}

/** Ordered STEP entity table → DATA section lines. */
class StepWriter {
    private lines: string[] = [];
    private nextId = 1;

    /** Add an entity; returns its reference id. */
    add(type: string, attrs: string[]): number {
        const id = this.nextId++;
        this.lines.push(`#${id}=${type}(${attrs.join(',')});`);
        return id;
    }

    /** Reference to a (possibly not-yet-created) entity slot. */
    ref(id: number): string {
        return `#${id}`;
    }

    render(header: { name: string; timestamp: string; author: string; organization: string }): string {
        const body = this.lines.join('\n');
        return `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('ViewDefinition [CoordinationView]'),'2;1');
FILE_NAME('${header.name.replace(/'/g, "''")}.ifc','${header.timestamp}',('${header.author}'),('${header.organization}'),'Yap IFC4 Exporter','Yap','');
FILE_SCHEMA(('IFC4'));
ENDSEC;
DATA;
${body}
ENDSEC;
END-ISO-10303-21;`;
    }
}

// ==================== Geometry frame helpers ====================

interface Vec3 { x: number; y: number; z: number }

function sub(a: Vec3, b: Vec3): Vec3 {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function normalize(v: Vec3): Vec3 {
    const len = Math.hypot(v.x, v.y, v.z);
    return len > 1e-9 ? { x: v.x / len, y: v.y / len, z: v.z / len } : { x: 0, y: 0, z: 1 };
}

/**
 * Shared solids factory: every body is an IfcExtrudedAreaSolid built inside
 * the element's local frame. Callers describe the profile and extrusion in
 * that frame; the factory owns the entity plumbing.
 */
class SolidBuilder {
    constructor(private w: StepWriter, private bodyContext: number) { }

    /** IfcAxis2Placement3D from location + optional axis/refDirection. */
    axis3(location: Vec3, axis?: Vec3, refDirection?: Vec3): number {
        const loc = this.w.add('IFCCARTESIANPOINT', [`(${num(location.x)},${num(location.y)},${num(location.z)})`]);
        const a = axis ? this.w.add('IFCDIRECTION', [`(${num(axis.x)},${num(axis.y)},${num(axis.z)})`]) : 0;
        const r = refDirection ? this.w.add('IFCDIRECTION', [`(${num(refDirection.x)},${num(refDirection.y)},${num(refDirection.z)})`]) : 0;
        return this.w.add('IFCAXIS2PLACEMENT3D', [this.w.ref(loc), a ? this.w.ref(a) : '$', r ? this.w.ref(r) : '$']);
    }

    /** Identity 3D placement (profile plane sits in the element frame). */
    private identity3(): number {
        return this.axis3({ x: 0, y: 0, z: 0 });
    }

    /** 2D profile position. */
    private axis2(x: number, y: number): number {
        const p = this.w.add('IFCCARTESIANPOINT', [`(${num(x)},${num(y)})`]);
        return this.w.add('IFCAXIS2PLACEMENT2D', [this.w.ref(p), '$']);
    }

    /** Rectangular cross-section extruded by `depth` along `dir` (local frame). */
    rectangle(xDim: number, yDim: number, depth: number, dir: Vec3 = { x: 0, y: 0, z: 1 }, profilePos: [number, number] = [0, 0]): number {
        const pos = this.axis2(profilePos[0], profilePos[1]);
        const profile = this.w.add('IFCRECTANGLEPROFILEDEF', ['.AREA.', '$', this.w.ref(pos), num(xDim), num(yDim)]);
        const d = this.w.add('IFCDIRECTION', [`(${num(dir.x)},${num(dir.y)},${num(dir.z)})`]);
        return this.w.add('IFCEXTRUDEDAREASOLID', [this.w.ref(profile), this.w.ref(this.identity3()), this.w.ref(d), num(depth)]);
    }

    /** Circular cross-section extruded along `dir`. */
    circle(radius: number, depth: number, dir: Vec3 = { x: 0, y: 0, z: 1 }): number {
        const pos = this.axis2(0, 0);
        const profile = this.w.add('IFCCIRCLEPROFILEDEF', ['.AREA.', '$', this.w.ref(pos), num(radius)]);
        const d = this.w.add('IFCDIRECTION', [`(${num(dir.x)},${num(dir.y)},${num(dir.z)})`]);
        return this.w.add('IFCEXTRUDEDAREASOLID', [this.w.ref(profile), this.w.ref(this.identity3()), this.w.ref(d), num(depth)]);
    }

    /** Arbitrary closed XY polygon extruded along Z (slabs, roofs). */
    polygon(points: [number, number][], depth: number): number {
        if (points.length < 3) return 0;
        const closed = [...points];
        const first = closed[0];
        const last = closed[closed.length - 1];
        if (Math.hypot(first[0] - last[0], first[1] - last[1]) > PRECISION) closed.push(first);
        const cps = closed.map(([x, y]) =>
            this.w.ref(this.w.add('IFCCARTESIANPOINT', [`(${num(x)},${num(y)})`]))
        );
        const poly = this.w.add('IFCPOLYLINE', [`(${cps.join(',')})`]);
        const profile = this.w.add('IFCARBITRARYCLOSEDPROFILEDEF', ['.AREA.', '$', this.w.ref(poly)]);
        const d = this.w.add('IFCDIRECTION', [`(${num(0)},${num(0)},${num(1)})`]);
        return this.w.add('IFCEXTRUDEDAREASOLID', [this.w.ref(profile), this.w.ref(this.identity3()), this.w.ref(d), num(depth)]);
    }

    /** Wrap solids into a product definition shape. */
    shape(solids: number[]): number {
        const reps = solids.filter(Boolean).map((solid) =>
            this.w.add('IFCSHAPEREPRESENTATION', [this.w.ref(this.bodyContext), str('Body'), str('SweptSolid'), `(${this.w.ref(solid)})`])
        );
        if (reps.length === 0) return 0;
        return this.w.add('IFCPRODUCTDEFINITIONSHAPE', ['$', '$', `(${reps.map((r) => this.w.ref(r)).join(',')})`]);
    }
}

// ==================== Category mapping ====================

/** App category → IFC4 product type. Unmapped categories become proxies. */
function ifcTypeForCategory(category: string): string {
    const mapping: Record<string, string> = {
        'Walls': 'IFCWALL',
        'Doors': 'IFCDOOR',
        'Windows': 'IFCWINDOW',
        'Floors': 'IFCSLAB',
        'Roofs': 'IFCROOF',
        'Structural Columns': 'IFCCOLUMN',
        'Structural Framing': 'IFCBEAM',
        'Ducts': 'IFCDUCTSEGMENT',
        'Pipes': 'IFCPIPESEGMENT',
        'Stairs': 'IFCSTAIR',
        'Railings': 'IFCRAILING',
        'Furniture': 'IFCFURNISHINGELEMENT',
        'Structural Foundations': 'IFCFOOTING',
        'Curtain Walls': 'IFCCURTAINWALL',
    };
    return mapping[category] ?? 'IFCBUILDINGELEMENTPROXY';
}

/**
 * IFC4 product attribute tuple. IfcBuildingElement base is
 * (GlobalId, OwnerHistory, Name, Description, ObjectType, ObjectPlacement,
 * Representation, Tag); type-specific trailing attributes follow the IFC4
 * schema (PredefinedType etc.) so attribute counts stay exact.
 */
function productAttrs(
    ifcType: string,
    ownerHistoryRef: string,
    name: string,
    placementRef: string | null,
    shapeRef: string | null
): string[] {
    const base = [str(generateGlobalId()), ownerHistoryRef, str(name), '$', '$', placementRef ?? '$', shapeRef ?? '$', '$'];
    switch (ifcType) {
        case 'IFCFURNISHINGELEMENT':
            return base; // no PredefinedType in IFC4
        case 'IFCDOOR':
            return [...base, '$', '$']; // PredefinedType, UserDefinedOperationType
        case 'IFCWINDOW':
            return [...base, '$', '$', '$']; // PredefinedType, PartitioningType, UserDefinedPartitioningType
        default:
            return [...base, '$']; // PredefinedType (IFC4 element types + proxy)
    }
}

// ==================== Element body builders ====================

/**
 * Build a swept-solid body for an element in its own local frame; returns
 * [shapeId, placementLocation, axis, refDirection]. `0` means "no body —
 * export placement-only" which stays schema-valid.
 */
function buildElementBody(w: StepWriter, sb: SolidBuilder, el: BIMElement): {
    shape: number;
    location: Vec3;
    axis?: Vec3;
    refDirection?: Vec3;
} | null {
    const g = (el as any).geometryData ?? {};
    const category = el.category;

    if (category === 'Walls' && Array.isArray(g.start) && Array.isArray(g.end)) {
        const start = { x: g.start[0], y: g.start[1], z: (g.start[2] ?? 0) + (g.baseOffset ?? 0) };
        const run = normalize(sub({ x: g.end[0], y: g.end[1], z: g.end[2] ?? 0 }, start));
        const length = Math.hypot(g.end[0] - start.x, g.end[1] - start.y, (g.end[2] ?? 0) - start.z);
        if (length < PRECISION) return null;
        const solid = sb.rectangle(length, g.thickness ?? 0.2, g.height ?? 3, { x: 0, y: 0, z: 1 }, [length / 2, 0]);
        return { shape: sb.shape([solid]), location: start, axis: { x: 0, y: 0, z: 1 }, refDirection: run };
    }

    if ((category === 'Floors' || category === 'Roofs') && Array.isArray(g.boundary)) {
        const baseZ = g.heightOffsetFromLevel ?? 0;
        const solid = sb.polygon(g.boundary, g.thickness ?? 0.2);
        if (!solid) return null;
        return { shape: sb.shape([solid]), location: { x: 0, y: 0, z: baseZ } };
    }

    if (category === 'Structural Columns' && Array.isArray(g.location)) {
        const loc = { x: g.location[0], y: g.location[1], z: (g.location[2] ?? 0) + (g.bottomOffset ?? 0) };
        const rot = g.rotation ?? 0;
        const width = Number(el.parameters?.['Width']) || 0.3;
        const depth = Number(el.parameters?.['Depth']) || 0.3;
        const solid = sb.rectangle(width, depth, g.height ?? 3);
        return {
            shape: sb.shape([solid]),
            location: loc,
            axis: { x: 0, y: 0, z: 1 },
            refDirection: { x: Math.cos(rot), y: Math.sin(rot), z: 0 },
        };
    }

    if (category === 'Structural Framing' && Array.isArray(g.start) && Array.isArray(g.end)) {
        const start = { x: g.start[0], y: g.start[1], z: g.start[2] ?? 0 };
        const run = normalize(sub({ x: g.end[0], y: g.end[1], z: g.end[2] ?? 0 }, start));
        const length = Math.hypot(g.end[0] - start.x, g.end[1] - start.y, (g.end[2] ?? 0) - start.z);
        if (length < PRECISION) return null;
        // Profile plane ⟂ run: local Z carries the run, local X stays up.
        const solid = sb.rectangle(g.width ?? 0.2, g.depth ?? 0.4, length, { x: 0, y: 0, z: 1 });
        return { shape: sb.shape([solid]), location: start, axis: run, refDirection: { x: 0, y: 0, z: 1 } };
    }

    if ((category === 'Ducts' || category === 'Pipes') && Array.isArray(g.path) && g.path.length >= 2) {
        const start = { x: g.path[0][0], y: g.path[0][1], z: g.path[0][2] ?? 0 };
        const run = normalize(sub({ x: g.path[1][0], y: g.path[1][1], z: g.path[1][2] ?? 0 }, start));
        const length = Math.hypot(g.path[1][0] - start.x, g.path[1][1] - start.y, (g.path[1][2] ?? 0) - start.z);
        if (length < PRECISION) return null;
        let solid = 0;
        if (category === 'Pipes' || g.shape === 'Round') {
            const diameter = g.diameter ?? Number(el.parameters?.['Diameter']) ?? 0.1;
            solid = sb.circle(diameter / 2, length, { x: 0, y: 0, z: 1 });
        } else {
            solid = sb.rectangle(g.width ?? 0.3, g.height ?? 0.3, length, { x: 0, y: 0, z: 1 });
        }
        return { shape: sb.shape([solid]), location: start, axis: run, refDirection: { x: 0, y: 0, z: 1 } };
    }

    // Generic point-placed categories (equipment, furniture, fixtures):
    // bounding-box body from width/depth/height parameters.
    const point = g.position ?? g.location ?? (Array.isArray(g.origin) && g.origin.length === 3 ? g.origin : null);
    if (Array.isArray(point)) {
        const width = Number(g.width ?? el.parameters?.['Width']) || 0;
        const depth = Number(g.depth ?? el.parameters?.['Depth']) || 0;
        const height = Number(g.height ?? el.parameters?.['Height']) || 0;
        if (width > PRECISION && depth > PRECISION && height > PRECISION) {
            const rot = g.rotation ?? 0;
            const solid = sb.rectangle(width, depth, height);
            return {
                shape: sb.shape([solid]),
                location: { x: point[0], y: point[1], z: point[2] ?? 0 },
                axis: { x: 0, y: 0, z: 1 },
                refDirection: { x: Math.cos(rot), y: Math.sin(rot), z: 0 },
            };
        }
        return { shape: 0, location: { x: point[0], y: point[1], z: point[2] ?? 0 } };
    }

    return null;
}

// ==================== Public entry ====================

/**
 * Export the project as a schema-valid IFC4 STEP document.
 */
export function exportToIFC(project: Project): string {
    const w = new StepWriter();

    // --- Owner history boilerplate ---
    const person = w.add('IFCPERSON', ['$', str('User'), '$', '$', '$', '$', '$', '$']);
    const org = w.add('IFCORGANIZATION', ['$', str('Yap'), '$', '$', '$']);
    const personOrg = w.add('IFCPERSONANDORGANIZATION', [w.ref(person), w.ref(org), '$']);
    const app = w.add('IFCAPPLICATION', [w.ref(org), '1.0', str('Yap'), str('Yap')]);
    const now = Math.floor(Date.now() / 1000);
    const ownerHistory = w.add('IFCOWNERHISTORY', [w.ref(personOrg), w.ref(app), '$', '.ADDED.', '$', '$', '$', String(now)]);

    // --- Units + geometric context ---
    const lengthUnit = w.add('IFCSIUNIT', ['*', '.LENGTHUNIT.', '$', '.METRE.']);
    const areaUnit = w.add('IFCSIUNIT', ['*', '.AREAUNIT.', '$', '.SQUARE_METRE.']);
    const volumeUnit = w.add('IFCSIUNIT', ['*', '.VOLUMEUNIT.', '$', '.CUBIC_METRE.']);
    const angleUnit = w.add('IFCSIUNIT', ['*', '.PLANEANGLEUNIT.', '$', '.RADIAN.']);
    const units = w.add('IFCUNITASSIGNMENT', [`(${[lengthUnit, areaUnit, volumeUnit, angleUnit].map((u) => w.ref(u)).join(',')})`]);

    const origin = w.add('IFCCARTESIANPOINT', [`(${num(0)},${num(0)},${num(0)})`]);
    const worldCS = w.add('IFCAXIS2PLACEMENT3D', [w.ref(origin), '$', '$']);
    const trueNorth = w.add('IFCDIRECTION', [`(${num(0)},${num(1)})`]);
    const context = w.add('IFCGEOMETRICREPRESENTATIONCONTEXT', ['$', str('Model'), '3', num(PRECISION), w.ref(worldCS), w.ref(trueNorth)]);
    const bodyContext = w.add('IFCGEOMETRICREPRESENTATIONSUBCONTEXT', [str('Body'), str('Model'), '*', '*', '*', '*', w.ref(context), '$', '.MODEL_VIEW.', '$']);
    const sb = new SolidBuilder(w, bodyContext);

    // --- Spatial hierarchy ---
    // IfcSite: (GlobalId, OwnerHistory, Name, Description, ObjectType,
    // ObjectPlacement, Representation, LongName, CompositionType,
    // RefLatitude, RefLongitude, Elevation, LandTitleNumber, SiteAddress).
    const projectEntity = w.add('IFCPROJECT', [str(generateGlobalId()), w.ref(ownerHistory), str(project.name), '$', '$', '$', '$', `(${w.ref(context)})`, w.ref(units)]);

    const sitePlacement = w.add('IFCLOCALPLACEMENT', ['$', w.ref(worldCS)]);
    const site = w.add('IFCSITE', [
        str(generateGlobalId()), w.ref(ownerHistory), str('Site'), '$', '$',
        w.ref(sitePlacement), '$', '$', '$', '$', '$', num(0), '$', '$',
    ]);

    // IfcBuilding: (…, LongName, CompositionType, ElevationOfRefHeight,
    // ElevationOfTerrain, BuildingAddress).
    const buildingPlacement = w.add('IFCLOCALPLACEMENT', [w.ref(sitePlacement), w.ref(worldCS)]);
    const building = w.add('IFCBUILDING', [
        str(generateGlobalId()), w.ref(ownerHistory), str('Building'), '$', '$',
        w.ref(buildingPlacement), '$', '$', '$', '$', '$', '$',
    ]);

    w.add('IFCRELAGGREGATES', [str(generateGlobalId()), w.ref(ownerHistory), '$', '$', w.ref(projectEntity), `(${w.ref(site)})`]);
    w.add('IFCRELAGGREGATES', [str(generateGlobalId()), w.ref(ownerHistory), '$', '$', w.ref(site), `(${w.ref(building)})`]);

    // Storeys from levels (kernel Map order).
    // IfcBuildingStorey: (…, LongName, CompositionType, Elevation,
    // ElevationRefDirection, TerrainElevation).
    const storeyPlacements = new Map<string, number>();
    const storeyEntities = new Map<string, number>();
    const storeyRefs: string[] = [];
    const storeyList: Array<{ id: string; name: string; elevation: number }> = [];
    for (const [id, level] of (project.levels as Map<string, any>)) {
        if (!level || typeof level !== 'object') continue;
        storeyList.push({ id, name: level.name ?? id, elevation: Number(level.elevation) || 0 });
    }
    if (storeyList.length === 0) {
        storeyList.push({ id: 'level-fallback', name: 'Ground Level', elevation: 0 });
    }
    for (const level of storeyList) {
        const loc = w.add('IFCCARTESIANPOINT', [`(${num(0)},${num(0)},${num(level.elevation)})`]);
        const axis = w.add('IFCAXIS2PLACEMENT3D', [w.ref(loc), '$', '$']);
        const placement = w.add('IFCLOCALPLACEMENT', [w.ref(buildingPlacement), w.ref(axis)]);
        const storey = w.add('IFCBUILDINGSTOREY', [
            str(generateGlobalId()), w.ref(ownerHistory), str(level.name), '$', '$',
            w.ref(placement), '$', '$', '$', num(level.elevation), '$', '$',
        ]);
        storeyPlacements.set(level.id, placement);
        storeyEntities.set(level.id, storey);
        storeyRefs.push(w.ref(storey));
    }
    w.add('IFCRELAGGREGATES', [str(generateGlobalId()), w.ref(ownerHistory), '$', '$', w.ref(building), `(${storeyRefs.join(',')})`]);
    const firstStoreyPlacement = storeyPlacements.get(storeyList[0].id)!;
    const firstStoreyEntity = storeyEntities.get(storeyList[0].id)!;

    // --- Elements ---
    /** storey entity → contained element refs. */
    const contained = new Map<number, string[]>();

    for (const el of project.elements.values()) {
        const ifcType = ifcTypeForCategory(el.category);
        const body = buildElementBody(w, sb, el);

        // Placement: element frame relative to its storey (omitted for
        // placement-only products so they still appear in viewers).
        let placementRef: string | null = null;
        if (body) {
            const axis = sb.axis3(body.location, body.axis, body.refDirection);
            const placement = w.add('IFCLOCALPLACEMENT', [w.ref(firstStoreyPlacement), w.ref(axis)]);
            placementRef = w.ref(placement);
        }

        const product = w.add(ifcType, productAttrs(ifcType, w.ref(ownerHistory), el.type ?? el.id, placementRef, body?.shape ? w.ref(body.shape) : null));

        const storeyId = resolveStoreyId(el, storeyList);
        const storeyEntity = storeyEntities.get(storeyId) ?? firstStoreyEntity;
        const bucket = contained.get(storeyEntity) ?? [];
        bucket.push(w.ref(product));
        contained.set(storeyEntity, bucket);
    }

    for (const [storeyEntity, refs] of contained) {
        w.add('IFCRELCONTAINEDINSPATIALSTRUCTURE', [str(generateGlobalId()), w.ref(ownerHistory), '$', '$', `(${refs.join(',')})`, w.ref(storeyEntity)]);
    }

    return w.render({
        name: project.name ?? 'Exported Model',
        timestamp: new Date().toISOString(),
        author: 'User',
        organization: 'Yap',
    });
}

/** Match an element to a storey via its level constraint or geometry levelId. */
function resolveStoreyId(el: BIMElement, storeyList: Array<{ id: string; name: string; elevation: number }>): string {
    const levelConstraint = el.constraints?.find((c) => c.type === 'LEVEL_BASED');
    if (levelConstraint?.targetId && storeyList.some((s) => s.id === levelConstraint.targetId)) {
        return levelConstraint.targetId;
    }
    const g = (el as any).geometryData ?? {};
    const geometryLevel = g.levelId ?? g.baseConstraintId;
    if (geometryLevel && storeyList.some((s) => s.id === geometryLevel)) {
        return geometryLevel;
    }
    return storeyList[0].id;
}
